/**
 * Centralized constants for the Quote Calculator.
 *
 * Keeps hardcoded sizing / shipping numbers in one spot so product ops
 * can tune them without chasing magic numbers scattered across JSX.
 */

// 8.5×11" letter page — used as both the base tile for Page-Maker products
// and the default maxW/maxH for sheet-cut items.
export const LETTER_PAGE_W_IN = 8.5;
export const LETTER_PAGE_H_IN = 11;

// Baseline sticker size that maps to a 1.0× size-price multiplier.
// Everything smaller/bigger scales via sqrt(area / BASE_AREA_IN2).
export const BASELINE_SIZE_IN = 2;
export const BASELINE_AREA_IN2 = BASELINE_SIZE_IN * BASELINE_SIZE_IN;

// Hardcoded fallback shipping used by the mocked customer-info page
// before the Freightcom quote arrives. Real rates come from the rate API.
export const FALLBACK_SHIPPING_COSTS_CAD = {
  standard: 4.99,
  rush: 19.99,
  express: 29.99,
};

export const SHIPPING_TIMES = {
  standard: '6 - 7 Days',
  rush: '4 - 5 Days',
  express: '1 - 2 Days',
};

// Per-product size limits. Used by Step4Size to surface a "switch to custom
// quote" banner when the customer tries to exceed the production machine
// max. Each sub-key is a `subcategory.id` in mockData.
export const SIZE_LIMITS = {
  stickers: {
    'custom-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'die-cut-singles': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'glitter-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'holographic-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'permanent-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'transparent-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
  },
  labels: {
    'clear-bopp-rolls': { maxW: 11, maxH: 30 },
    'white-bopp-rolls': { maxW: 11, maxH: 30 },
    'gold-silver-foil': { maxW: 11, maxH: 30 },
    'semigloss-paper': { maxW: 11, maxH: 30 },
    'white-vinyl-pages': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'writable-bopp': { maxW: 11, maxH: 30 },
  },
  decals: {
    'clear-decals': { maxW: 48, maxH: 70 },
    'indoor-floor-decals': { maxW: 48, maxH: 60 },
    'static-clings': { maxW: 24, maxH: 24 },
    'wall-decals': { maxW: 48, maxH: 60 },
    'vinyl-graphics': { maxW: 48, maxH: 60 },
  },
  'iron-ons': {
    'classic-tattoos': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'dtf-heat-transfers': { maxW: 28, maxH: 72 },
    'dtf-by-sheet': { maxW: 28, maxH: 72 },
    'emblems-patches': { maxW: 6, maxH: 6 },
    'name-badges': { maxW: 4, maxH: 4 },
  },
  magnets: {
    magnets: { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'vehicle-magnets': { maxW: 72, maxH: 40 },
    'glow-in-dark': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
    'uv-dtf-stickers': { maxW: LETTER_PAGE_W_IN, maxH: LETTER_PAGE_H_IN },
  },
};

/**
 * Look up the (maxW, maxH) size limit for a product+format. Returns null
 * when no limit is registered — caller should treat unknown combos as
 * unrestricted (no warning banner shown).
 */
export const getSizeLimit = (productId, formatId) =>
  SIZE_LIMITS[productId]?.[formatId] || null;
